import { customElement, property, query, state } from 'lit/decorators.js'
import { CSSResultGroup, html, LitElement, nothing, TemplateResult } from 'lit'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import {
  OPEN_BASKET_STOP_EDITING_BUTTON_API,
  OpenBasketStopEditingButtonAPI
} from 'components/ordering/basket-support/open-basket-stop-editing-button/open-basket-stop-editing-button-api.js'
import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { WINDOW_LOCATION, WindowLocation } from '@cloudsuite/mosaic-shared/scripts/window-location.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { styles } from 'components/ordering/basket-support/open-basket-stop-editing-button/open-basket-stop-editing-button.style.js'

type componentType = 'button' | 'link'

@customElement('csc-open-basket-stop-editing-button')
export class CSCOpenBasketStopEditingButton extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, styles]

  @property({ type: String })
  variant: componentType = 'button'

  @property({ attribute: 'basket-index-url' })
  basketIndexUrl?: string

  @property({ attribute: 'return-url' })
  returnUrl?: string

  @property({ type: String, attribute: 'basket-id' })
  basketId?: string

  @inject(WINDOW_LOCATION)
  private windowLocation!: WindowLocation

  @inject(OPEN_BASKET_STOP_EDITING_BUTTON_API)
  private openBasketStopEditingButtonAPI!: OpenBasketStopEditingButtonAPI

  @state()
  private apiCallFailedMessage = false

  @query('cse-button')
  private button!: HTMLButtonElement

  override async connectedCallback() {
    super.connectedCallback()
    if (this.basketIndexUrl === undefined) {
      throw new Error('missing basket-index-url attribute')
    }

    if (this.basketId === undefined) {
      throw new Error('missing basket-id attribute')
    }
  }

  private async toggleDisable() {
    if (this.variant === 'button') {
      this.button.disabled = !this.button.disabled
      return
    }
  }

  private async stopViewBasket(): Promise<void> {
    this.apiCallFailedMessage = false
    await this.toggleDisable()

    if (this.basketId === undefined) {
      this.apiCallFailedMessage = true
      await this.toggleDisable()
      return
    }
    const stoppedViewBasket = await this.openBasketStopEditingButtonAPI.stopViewBasket(this.basketId)
    const nextUrl= this.returnUrl || this.basketIndexUrl
    if (stoppedViewBasket.success && nextUrl !== undefined) {
      this.windowLocation.redirect(new URL(nextUrl, window.location.href))
      return
    }

    await this.toggleDisable()
    this.apiCallFailedMessage = true
  }

  override render(): TemplateResult {
    return html`
      ${this.variant === 'button' ? html`
        <cse-button
          type="button"
          variant="warning"
          size="small"
          @click="${this.stopViewBasket}"
        >
          ${t('stop_editing', { ns: 'csc-open-basket-stop-editing-button', default: 'Stop editing' })}
        </cse-button>
      ` : html`
        <cse-link
          @click="${this.stopViewBasket}"
        >
          ${t('stop_editing', { ns: 'csc-open-basket-stop-editing-button', default: 'Stop editing' })}
        </cse-link>
      `}

      ${this.apiCallFailedMessage ? html`
        <cse-text as="p" variant="body-s">
          ${t('api_call_failed_message', {
            ns: 'csc-open-basket-stop-editing-button',
            default: 'Stop editing basket failed, please try again.'
          })}
        </cse-text>
      ` : nothing}

    `
  }

}
